import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useDashboardData } from './DashboardProvider';
import useTranslation from '../customHooks/translations';


const StatsBox = () => {
  const { statsObject } = useDashboardData();
  const {dashboardPage: translation} = useTranslation();

  function formatNumber(num) {
    if (num == null || isNaN(num)) return '0 g';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(0) + ' tonnes';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(0) + ' kg';
    } else {
      return num.toFixed(0) + ' g';
    }
  }

  function formatPoints(num) {
    if (num == null || isNaN(num)) return '0';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(0) + ' M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(0) + ' K';
    } else {
      return num.toLocaleString();
    }
  }

  const statCards = [
    {
      value: statsObject?.number_of_actions,
      label: translation.actionsTaken,
      bg: '#F0FDFA',
      accent: '#0D9488',
    },
    {
      value: formatNumber(statsObject?.weekly_co2),
      label: translation.weeklyCO2,
      bg: '#FFF7ED',
      accent: '#EA580C',
    },
    {
      value: formatNumber(statsObject?.total_co2),
      label: translation.totalCO2Saved,
      bg: '#F0FDF4',
      accent: '#16A34A',
    },
    {
      value: statsObject?.streak,
      label: translation.currentStreak,
      bg: '#F1F5F9',
      accent: '#0F172A',
    },
  ];

  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
      gap: '16px',
      alignItems: 'center',
      '& > *:first-of-type': {
        gridColumn: { md: '1 / 3' },
      },
    }}>
      {/* Hero points card */}
      <Paper sx={{
        borderRadius: '16px',
        background: 'linear-gradient(135deg, #0F766E 0%, #0D9488 40%, #14B8A6 100%)',
        padding: '1.75em 1.5em',
        textAlign: 'center',
        color: '#FFFFFF',
        lineHeight: '1.2em',
        border: 'none',
        boxShadow: '0 4px 20px rgba(13,148,136,0.2)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          right: '-20%',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.08)',
        },
      }}>
        <Typography sx={{
          fontWeight: 800,
          paddingBottom: '12px',
          fontSize: '1.5em',
          fontFamily: '"Outfit", "DM Sans", sans-serif',
          color: '#FFFFFF',
          letterSpacing: '-0.02em',
        }}>
          {translation.totalPointsColon} {formatPoints(statsObject?.total_points)}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
          {translation.thisWeek}
          <br/>
          <TrendingUpIcon sx={{ color: '#5EEAD4', marginBottom: '-4px', fontSize: '1.2rem' }}/>{' '}
          <strong style={{ color: '#FFFFFF' }}>{formatPoints(statsObject?.weekly_points)}</strong>
        </Typography>
      </Paper>

      {/* Stat mini-cards */}
      {statCards.map((stat, i) => (
        <Paper key={i} sx={{
          padding: '1.25rem',
          background: stat.bg,
          border: 'none',
          borderRadius: '14px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: '6px',
          boxShadow: 'none',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
          },
        }}>
          <Typography sx={{
            color: stat.accent,
            fontSize: '1.75rem',
            fontWeight: 800,
            fontFamily: '"Outfit", "DM Sans", sans-serif',
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
          }}>
            {stat.value}
          </Typography>
          <Typography sx={{
            color: '#64748B',
            fontSize: '0.8rem',
            fontWeight: 500,
          }}>
            {stat.label}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
};

export default StatsBox;
